// src/pages/admin/ManagePendaftar.js
import React, { useEffect, useState } from "react";
import { db } from "../../firebase";
import { collection, getDocs, doc, updateDoc } from "firebase/firestore";
import Swal from "sweetalert2";
import { FaFilePdf, FaDownload, FaFileExport, FaEye } from "react-icons/fa";
import * as XLSX from "xlsx";

export default function ManagePendaftar() {
  const [pendaftar, setPendaftar] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("Semua");

  const [reviewData, setReviewData] = useState(null);
  const [modalImg, setModalImg] = useState(null);
  const [modalPdf, setModalPdf] = useState(null);

  /* ================= UTIL ================= */
  const isUrl = (v) => typeof v === "string" && v.startsWith("http");
  const isBase64 = (v) => typeof v === "string" && v.length > 100;

  const openPdf = (file) => {
    if (isUrl(file)) setModalPdf(file);
    else setModalPdf(`data:application/pdf;base64,${file}`);
  };

  const downloadFile = (file, name) => {
    if (isUrl(file)) {
      const a = document.createElement("a");
      a.href = file;
      a.download = name;
      a.target = "_blank";
      a.click();
    } else {
      const a = document.createElement("a");
      a.href = `data:application/octet-stream;base64,${file}`;
      a.download = name;
      a.click();
    }
  };

  /* ================= FETCH ================= */
  useEffect(() => {
    const fetchData = async () => {
      const snap = await getDocs(collection(db, "ppdb"));
  
      // Filter dokumen yang punya field minimal nama & nisn
      const validData = snap.docs
        .map((d) => ({ id: d.id, ...d.data() }))
        .filter((p) => p.nama && p.nisn); // Hanya yang ada nama & nisn
  
      setPendaftar(validData);
      setLoading(false);
    };
    fetchData();
  }, []);
  
  
  const handleStatus = async (id, status) => {
    await updateDoc(doc(db, "ppdb", id), { status });
    setPendaftar((p) => p.map((x) => (x.id === id ? { ...x, status } : x)));
    Swal.fire("Berhasil", "Status diperbarui", "success");
  };

  /* ================= EXPORT ================= */
  const exportExcel = () => {
    const data = pendaftar.map((p, i) => ({
      No: i + 1,
      Nama: p.nama,
      NISN: p.nisn,
      Asal_Sekolah: p.asal_sekolah,
      Nilai: p.nilai_rata,
      Status: p.status || "Menunggu",
    }));
    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "PPDB");
    XLSX.writeFile(wb, "ppdb.xlsx");
  };

  const filtered = filter === "Semua" ? pendaftar : pendaftar.filter((p) => p.status === filter);

  if (loading) return <p className="text-center mt-5">Memuat data...</p>;

  /* ================= UI ================= */
  return (
    <div className="container mt-4">
      <h3 className="mb-3">Manajemen Pendaftar PPDB</h3>

      <div className="d-flex justify-content-between mb-3">
        <select className="form-select w-auto" value={filter} onChange={(e) => setFilter(e.target.value)}>
          <option>Semua</option>
          <option>Menunggu</option>
          <option>Diterima</option>
          <option>Ditolak</option>
        </select>

        <button className="btn btn-success" onClick={exportExcel}>
          <FaFileExport /> Export Excel
        </button>
      </div>

      {/* ===== TABLE ===== */}
      <div className="table-responsive">
        <table className="table table-bordered align-middle">
          <thead className="table-primary">
            <tr>
              <th>No</th>
              <th>Nama</th>
              <th>NISN</th>
              <th>Asal Sekolah</th>
              <th>Nilai</th>
              <th>Status</th>
              <th>Berkas</th>
              <th>Aksi</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((p, i) => (
              <tr key={p.id}>
                <td>{i + 1}</td>
                <td>{p.nama}</td>
                <td>{p.nisn}</td>
                <td>{p.asal_sekolah}</td>
                <td>{p.nilai_rata}</td>
                <td>
                  <span className={`badge bg-${p.status === "Diterima" ? "success" : p.status === "Ditolak" ? "danger" : "secondary"}`}>
                    {p.status || "Menunggu"}
                  </span>
                </td>
                <td>
                  Pas Foto: {p.pasFoto ? "Ada" : "—"} <br />
                  KK: {p.fotoKK ? "Ada" : "—"} <br />
                  Rapor: {p.fotoRapor ? "Ada" : "—"}
                </td>
                <td>
                  <button className="btn btn-sm btn-primary me-1" onClick={() => setReviewData(p)}>
                    <FaEye /> Review
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* ===== MODAL REVIEW ===== */}
      {reviewData && (
        <div className="modal-overlay">
          <div className="modal-box">
            <h5 className="mb-3">Review Pendaftar</h5>

            <p><b>Nama:</b> {reviewData.nama}</p>
            <p><b>Email:</b> {reviewData.email}</p>
            <p><b>NISN:</b> {reviewData.nisn}</p>
            <p><b>Lahir:</b> {reviewData.tempat_lahir}, {reviewData.tanggal_lahir}</p>
            <p><b>Jenis Kelamin:</b> {reviewData.jenis_kelamin}</p>
            <p><b>Asal Sekolah:</b> {reviewData.asal_sekolah}</p>
            <p><b>Nilai:</b> {reviewData.nilai_rata}</p>

            <hr />
            <h6>Orang Tua</h6>
            <p>Ayah: {reviewData.nama_ayah} ({reviewData.pekerjaan_ayah})</p>
            <p>Ibu: {reviewData.nama_ibu} ({reviewData.pekerjaan_ibu})</p>

            <hr />
            <h6>Berkas</h6>

            {["pasFoto","fotoKK","fotoRapor","ijazah","piagam"].map(k => {
              const f = reviewData[k];
              if (!f) return null;
              const isPdf = f.toLowerCase?.().includes("pdf");
              return (
                <div key={k} className="mb-2">
                  <b>{k}</b><br />
                  {isPdf ? (
                    <button className="btn btn-sm btn-outline-danger me-2" onClick={() => openPdf(f)}>
                      <FaFilePdf /> Lihat PDF
                    </button>
                  ) : (
                    <img src={isUrl(f) ? f : `data:image/jpeg;base64,${f}`} width={80}
                      onClick={() => setModalImg(isUrl(f) ? f : `data:image/jpeg;base64,${f}`)}
                      style={{ cursor: "pointer" }} />
                  )}
                  <button className="btn btn-sm btn-success ms-2" onClick={() => downloadFile(f, `${reviewData.nama}_${k}`)}>
                    <FaDownload /> Download
                  </button>
                </div>
              );
            })}

            <hr />
            <div className="d-flex gap-2">
              <button className="btn btn-success" onClick={() => handleStatus(reviewData.id, "Diterima")}>Diterima</button>
              <button className="btn btn-danger" onClick={() => handleStatus(reviewData.id, "Ditolak")}>Ditolak</button>
              <button className="btn btn-secondary" onClick={() => handleStatus(reviewData.id, "Menunggu")}>Menunggu</button>
              <button className="btn btn-outline-dark ms-auto" onClick={() => setReviewData(null)}>Tutup</button>
            </div>
          </div>
        </div>
      )}

      {/* IMAGE MODAL */}
      {modalImg && (
        <div className="modal-overlay" onClick={() => setModalImg(null)}>
          <img src={modalImg} style={{ maxHeight: "90vh" }} />
        </div>
      )}

      {/* PDF MODAL */}
      {modalPdf && (
        <div className="modal-overlay" onClick={() => setModalPdf(null)}>
          <iframe src={modalPdf} style={{ width: "90vw", height: "90vh" }} />
        </div>
      )}

      <style>{`
        .modal-overlay{
          position:fixed; inset:0;
          background:rgba(0,0,0,.6);
          display:flex; justify-content:center; align-items:center;
          z-index:2000;
        }
        .modal-box{
          background:#fff; padding:20px;
          width:90%; max-width:800px;
          border-radius:10px;
          max-height:90vh; overflow:auto;
        }
      `}</style>
    </div>
  );
}
